import { TvvStandaardRoute } from "@/entities/tvv";
import { RouteBewerkenDialog } from "@/features/tvv/components/regievoerder/route-bewerken";
import { RouteTags, RouteTimeline, RouteTitel } from "@/features/tvv/components/shared";
import { Button, Checkbox, Collapsible, CollapsibleContent, CollapsibleTrigger, Icon } from "@/shared/components/ui";
import { cn } from "@/shared/utils";
import { useState } from "react";
import type { UseFormReturn } from "react-hook-form";
import { BestellingFormSection } from "./bestelling-form-section";
import type { BestellingenFormData } from "./schema";

type RouteBestellingItemProps = {
    route: TvvStandaardRoute;
    index: number;
    form: UseFormReturn<BestellingenFormData>;
    defaultOpen?: boolean;
    onRouteEdited: (updatedRoute: TvvStandaardRoute) => void;
};

export function RouteBestellingItem({ route, index, form, defaultOpen = false, onRouteEdited }: Readonly<RouteBestellingItemProps>) {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    const [editDialogOpen, setEditDialogOpen] = useState(false);

    const voorwaardeError = form.formState.errors.bestellingen?.[index]?.voorwaardeGeaccepteerd;

    return (
        <Collapsible open={isOpen} onOpenChange={setIsOpen} className="border-t" data-testid={`route-${route.stationCodes.join(">")}`}>
            <CollapsibleTrigger className="flex w-full items-center justify-between py-4">
                <div className="flex w-full items-center justify-between gap-3">
                    <RouteTitel route={route} />
                    <Icon name="chevron-down" size="m" className={cn("transition-transform", isOpen && "rotate-180")} />
                </div>
            </CollapsibleTrigger>

            <CollapsibleContent preventFocusClipping>
                <div className="space-y-4">
                    {route.opmerking && (
                        <div className="space-y-1">
                            <div className="text-s font-medium">Opmerkingen</div>
                            <p className="whitespace-pre-line">{route.opmerking.replaceAll(/\\\\n/g, "\n")}</p>
                        </div>
                    )}

                    {route.voorwaarde && (
                        <div className="space-y-2">
                            <div className="text-s font-medium">Voorwaarden</div>
                            <Checkbox
                                checked={form.watch(`bestellingen.${index}.voorwaardeGeaccepteerd`)}
                                onCheckedChange={(checked) =>
                                    form.setValue(`bestellingen.${index}.voorwaardeGeaccepteerd`, checked === true)
                                }
                                className="mt-0.5"
                            />
                            <span className="ml-2">{route.voorwaarde}</span>
                            {voorwaardeError && <p className="text-destructive text-s">Accepteer de voorwaarde om te bestellen</p>}
                        </div>
                    )}

                    <Button type="button" variant="tertiary" size="s" icon="route" onClick={() => setEditDialogOpen(true)}>
                        Route bewerken
                    </Button>

                    <RouteBewerkenDialog open={editDialogOpen} onOpenChange={setEditDialogOpen} route={route} onApply={onRouteEdited} />

                    <RouteTags organisatie={route.organisatie} voertuigSoort={route.voertuigSoort} />

                    <RouteTimeline stationCodes={route.stationCodes} rijtijden={route.rijtijden} />

                    <BestellingFormSection route={route} index={index} form={form} />
                </div>
            </CollapsibleContent>
        </Collapsible>
    );
}
